import React, { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap";

import TableContainer from "Components/Common/TableContainer";
import {
  getContracts,
  deleteContract,
} from "common/services/contracts.service";
import AddContract from "./AddContract";
import { getSortObj } from "common/lib/tableDataOptions";
import { createTableSearchParams, parseTableFilters, parseTableSearch, parseTableSort } from "common/lib/tableQueryParams";
import * as moment from "moment";
import { isContractActive } from "common/lib/contractOptions";
import UpdateContract from "./UpdateContract";
import Permission from "Components/Common/Permission";
import Permissions from "Components/Common/Permissions";
import DeleteModal from "Components/Common/DeleteModal";
import BulkActionBar from "Components/Common/BulkActionBar";
import { normalize } from "common/lib/normalize";
import { clearTableCheckboxes } from "common/lib/tableSelection";
import { toast } from "react-toastify";
import Import from "./Import";
import { useProfile } from "Components/Hooks/UserHooks";
import { isAllowed } from "common/lib/permissionUtils";
import Filters from "./Filters";

interface DateRange {
  fromDate: Date | null;
  toDate: Date | null;
}

interface FilterValue {
  contractType: string;
  agency: string;
  dateRange: DateRange;
}

// @ts-ignore
const TextWrapper: React.FC<{ value: string }> = ({ value }) => {
  if (!value) return null;
  // const matches = value.match(/.{1,40}/g);
  // const wrappedText = matches ? matches.join("\n") : ""; // Handle null case
  return <div style={{ whiteSpace: "pre-wrap", width: "220px" }}>{value}</div>;
};

const AllContracts = ({
  isViewContractors,
  contractorId,
}: {
  isViewContractors?: boolean;
  isAgencyDashboard?: boolean;
  contractorId?: string;
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [params] = useState({
    page: Number(searchParams.get("page")) || 1, // Starts from 1
    pageSize: Number(searchParams.get("pageSize")) || 20, // Default page size
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  // Update pageIndex
  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  const [sorted, setSorted] = useState(() =>
    parseTableSort(searchParams, [{ id: "createdAt", desc: true }])
  );

  const [selectedRecord, setSelectedRecord] = useState<any>({});

  const fetchContracts = () => {
    setPage(1);
    setSorted([{ id: "createdAt", desc: true }]);
    setSearchValue("");
    setFilterValue(defaultFilterValue);
  };

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const showAddModalForm = () => {
    setIsAddModalOpen(true);
  };

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const showUpdateModalForm = (contract: any) => {
    setSelectedRecord(contract);
    setIsUpdateModalOpen(true);
  };

  const [searchValue, setSearchValue] = useState(parseTableSearch(searchParams));
  const defaultFilterValue: FilterValue = {
    contractType: "",
    agency: "",
    dateRange: {
      fromDate: null,
      toDate: null,
    },
  };
  const [filterValue, setFilterValue] = useState(() =>
    parseTableFilters(searchParams, defaultFilterValue)
  );

  const unCheckAll = useCallback(() => {
    clearTableCheckboxes();
    setSelectedCheckBox([]);
  }, []);

  const [apiParams, setApiParams] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      const params = createTableSearchParams({
        page,
        pageSize: pagination.pageSize,
        sorted,
        searchValue,
        filterValue,
      });
      setSearchParams(params, { replace: true });

      const sortObj = getSortObj(sorted);

      let andConditions = [];

      if (isViewContractors) {
        andConditions.push({
          contractor: {
            id: {
              $eq: contractorId,
            },
          },
        });
      }
      if (filterValue?.agency) {
        andConditions.push({
          agency: {
            id: {
              $eq: filterValue.agency,
            },
          },
        });
      }
      if (filterValue?.contractType) {
        andConditions.push({
          contractType: {
            id: {
              $eq: filterValue.contractType,
            },
          },
        });
      }
      if (filterValue?.dateRange?.fromDate && filterValue?.dateRange?.toDate) {
        const fromDate = new Date(
          filterValue.dateRange.fromDate
        ).toLocaleDateString("en-CA");
        const toDate = new Date(
          filterValue.dateRange.toDate
        ).toLocaleDateString("en-CA");
        // Contract overlaps filter if contract.startDate <= filter.toDate AND contract.endDate >= filter.fromDate
        andConditions.push({
          startDate: { $lte: toDate },
          endDate: { $gte: fromDate },
        });
      }
      if (filterValue?.isLOA === true) {
        andConditions.push({
          isLOA: true,
        });
      }
      if (filterValue?.underLTA === true) {
        // Contracts awarded under a Long Term Agreement.
        andConditions.push({
          lta: { id: { $notNull: true } },
        });
      }

      andConditions.push({
        $or: [
          {
            contractTitle: {
              $containsi: searchValue,
            },
          },
          {
            contractor: {
              contractorName: {
                $containsi: searchValue,
              },
            },
          },
        ],
      });

      try {
        setLoading(true);
        unCheckAll();
        const apiParams = {
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          populate: [
            "agency",
            "contractType",
            "contractRating",
            "subContractedTo",
            "contractor",
            "location",
            "lta",
          ],
          filters: {
            $and: andConditions,
          },
        };
        setApiParams(apiParams);
        const response: any = await getContracts(apiParams);
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [
      contractorId,
      isViewContractors,
      pagination.pageSize,
      setSearchParams,
      unCheckAll,
    ]
  );

  const [deleteModal, setDeleteModal] = useState(false);

  const onClickDelete = useCallback(
    (data: any) => {
      setSelectedRecord(data);
      setDeleteModal(true);
    },
    [setSelectedRecord, setDeleteModal]
  );

  const fetchUpdatedData = useCallback(() => {
    onPageChange({
      page: pagination.pageIndex + 1,
      sorted: sorted,
      searchValue: searchValue,
      filterValue: filterValue,
    });
  }, [onPageChange, pagination.pageIndex, sorted, searchValue, filterValue]);

  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    if (selectedRecord) {
      setDeleting(true);
      try {
        await deleteContract(selectedRecord.id);
        fetchUpdatedData();
        setDeleting(false);
        setDeleteModal(false);
        toast.success("Contract deleted successfully");
      } catch (e: any) {
        toast.error("Error while deleting contract");
      } finally {
        setDeleting(false);
      }
    }
  };

  const checkedAll = useCallback(() => {
    const checkall: any = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".tableCheckbox");

    if (checkall.checked) {
      ele.forEach((ele: any) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele: any) => {
        ele.checked = false;
      });
    }
    onSelectCheckbox();
  }, []);

  const [selectedCheckBox, setSelectedCheckBox] = useState<any>([]);

  const onSelectCheckbox = () => {
    const ele = document.querySelectorAll(".tableCheckbox:checked");
    setSelectedCheckBox(ele);
  };

  /* Delete Multiple */
  const [deleteMultipleModal, setDeleteMultipleModal] = useState(false);
  const handleDeleteMultiple = async () => {
    const selectedIds = Array.from(selectedCheckBox).map((ele: any) => {
      return ele.value;
    });
    if (selectedIds.length > 0) {
      setDeleting(true);
      try {
        await Promise.all(
          selectedIds.map((id: any) => {
            return deleteContract(id);
          })
        );
        setDeleting(false);
        toast.success("Contracts deleted successfully.");
        fetchUpdatedData();
        setDeleteMultipleModal(false);
      } catch (e: any) {
        toast.error("Error while deleting data.");
      } finally {
        setDeleting(false);
      }
    }
  };

  const { userProfile } = useProfile();
  const [columnVisibility] = useState({
    checkBoxColumn: isAllowed(userProfile?.permissions, [
      {
        resource: "contract",
        action: "delete",
      },
    ]),
    updatedAt: isAllowed(userProfile?.permissions, [
      {
        resource: "contract",
        action: "update",
      },
    ]),
    createdAt: isAllowed(userProfile?.permissions, [
      {
        resource: "contract",
        action: "create",
      },
    ]),
  });

  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            className="form-check-input"
            onClick={() => checkedAll()}
          />
        ),
        cell: (cell: any) => {
          return (
            <input
              type="checkbox"
              className="tableCheckbox form-check-input"
              value={cell.getValue()}
              onChange={() => onSelectCheckbox()}
            />
          );
        },
        id: "checkBoxColumn",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
        meta: { cellClass: "table-checkbox-column" },
      },
      {
        header: "Contract Title",
        accessorKey: "contractTitle",
        id: "contractTitle",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return (
            <div style={{ whiteSpace: "pre-wrap", width: "300px" }}>
              <Link
                to={`/contracts/${cell.row.original?.id}`}
                className="fw-medium link-primary"
              >
                {cell.getValue()}
              </Link>
            </div>
          );
        },
      },

      {
        header: "Contractor Name",
        accessorKey: "contractor.contractorName",
        enableColumnFilter: false,
        id: "contractor.contractorName",
        cell: (cell: any) => (
          <TextWrapper value={cell.getValue() || "Not Provided"} />
        ),
      },
      {
        header: "UN Agency",
        accessorKey: "agency.name",
        id: "agency.name",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return <>{cell.getValue() || "Not Provided"}</>;
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const status = isContractActive(
            cell.row.original.startDate || "",
            cell.row.original.endDate || ""
          );
          return (
            <React.Fragment>
              {status === null ? (
                <span className="badge bg-warning-subtle text-warning fs-11">
                  N/A
                </span>
              ) : status ? (
                <span className="badge bg-success-subtle text-success fs-11">
                  Active
                </span>
              ) : status === false ? (
                <span className="badge bg-danger-subtle text-danger fs-11">
                  Not Active
                </span>
              ) : null}
            </React.Fragment>
          );
        },
      },
      {
        header: "Amount",
        accessorKey: "contractValue",
        enableColumnFilter: false,
        id: "contractValue",
        cell: (cell: any) => {
          return (
            <>
              {cell.getValue()
                ? `$ ${cell.getValue().toLocaleString()}`
                : "Not Provided"}
            </>
          );
        },
      },

      {
        header: "Contract Type",
        accessorKey: "contractType.name",
        enableColumnFilter: false,
        id: "contractType.name",
        cell: (cell: any) => {
          return <>{cell.getValue() || "Not Provided"}</>;
        },
      },

      {
        header: "LTA",
        accessorKey: "lta.title",
        enableColumnFilter: false,
        id: "lta.title",
        cell: (cell: any) => {
          const lta = cell.row.original?.lta;
          if (!lta) return <span className="text-muted">—</span>;
          return (
            <Link to={`/ltas/${lta.id}`} className="link-primary">
              {lta.ltaNumber || lta.title}
            </Link>
          );
        },
      },

      {
        header: "Contract Start Date",
        accessorKey: "startDate",
        id: "startDate",
        enableColumnFilter: false,
        cell: (cell: any) => {
          if (cell.getValue() === null) return "Not Provided";
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return <>{date}</>;
        },
      },

      {
        header: "Contract End Date",
        accessorKey: "endDate",
        id: "endDate",
        enableColumnFilter: false,
        cell: (cell: any) => {
          if (cell.getValue() === null) return "Not Provided";
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return <>{date}</>;
        },
      },

      {
        header: "Last Updated",
        accessorKey: "updatedAt",
        id: "updatedAt",
        enableColumnFilter: false,
        cell: (cell: any) => {
          if (cell.getValue() === null) return null;
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return <>{date}</>;
        },
      },

      {
        header: "Created At",
        accessorKey: "createdAt",
        id: "createdAt",
        enableColumnFilter: false,
        cell: (cell: any) => {
          if (cell.getValue() === null) return null;
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return <>{date}</>;
        },
      },

      {
        header: "Action",
        enableSorting: false,
        cell: (cellProps: any) => {
          const rowData = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn btn-soft-secondary btn-sm dropdown"
                tag="button"
              >
                <i className="ri-more-fill align-middle"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <Permission resource="contract" action="findOne">
                  <DropdownItem
                    href={`#`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/contracts/${rowData.id}`);
                    }}
                  >
                    <i className="ri-eye-fill align-bottom me-2 text-muted"></i>{" "}
                    View
                  </DropdownItem>
                </Permission>

                <Permission resource="contract" action="update">
                  <DropdownItem
                    href="/#"
                    onClick={(e) => {
                      e.preventDefault();
                      showUpdateModalForm({
                        id: rowData.id,
                        ...rowData,
                      });
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>{" "}
                    Edit
                  </DropdownItem>
                </Permission>

                <Permission resource="contract" action="delete">
                  <DropdownItem divider />
                  <DropdownItem
                    href="#"
                    onClick={() => {
                      onClickDelete(rowData);
                    }}
                  >
                    <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{" "}
                    Delete
                  </DropdownItem>
                </Permission>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [checkedAll, navigate, onClickDelete]
  );

  return (
    <React.Fragment>
      <Filters
        setSearchValue={setSearchValue}
        setPage={setPage}
        searchValue={searchValue}
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        setSorted={setSorted}
      />

      <Card>
        <CardHeader>
          <Row className="align-items-center g-3">
            <Col md={8}>
              <h5 className="card-title mb-1">Contracts</h5>
              <p className="text-muted mb-0">
                This is a detailed list of contracts awarded to contractors,
                vendors, and implementing partners by various UN agencies.
              </p>
            </Col>
            <div className="col-md-auto ms-auto">
              <div className="d-flex gap-2">
                <Permission resource="contract" action="create">
                  <Link
                    to="#"
                    className="btn btn-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      showAddModalForm();
                    }}
                  >
                    <i className="ri-add-line align-bottom me-1"></i>
                    Add Contract
                  </Link>
                </Permission>

                <Permissions
                  checks={[
                    {
                      resource: "contract",
                      action: "import",
                    },
                    {
                      resource: "contract",
                      action: "export",
                    },
                  ]}
                >
                  <Import apiParams={apiParams} onImported={fetchUpdatedData} />
                </Permissions>
              </div>
            </div>
          </Row>
        </CardHeader>
        <CardBody>
          <TableContainer
            loading={loading}
            columns={columns}
            data={data || []}
            pagination={pagination}
            setPagination={setPagination}
            sorted={sorted}
            setSorted={setSorted}
            onPageChange={onPageChange}
            FilterSection={() => {}}
            totalRows={meta?.pagination?.total || 0}
            totalPages={meta?.pagination?.pageCount || 0}
            searchValue={searchValue}
            filterValue={filterValue}
            columnVisibility={columnVisibility}
            divClass="table-responsive table-card mb-4"
            tableClass="align-middle table-nowrap mb-0 table-striped"
            theadClass="table-light table-nowrap"
          />
          <AddContract
            toggle={() => setIsAddModalOpen((state) => !state)}
            isModalOpen={isAddModalOpen}
            fetchContracts={fetchContracts}
          />
          <UpdateContract
            toggle={() => setIsUpdateModalOpen((state) => !state)}
            isModalOpen={isUpdateModalOpen}
            selectedRecord={selectedRecord}
            fetchContracts={fetchUpdatedData}
          />
          <DeleteModal
            show={deleteModal}
            onDeleteClick={() => handleDelete()}
            onCloseClick={() => setDeleteModal(false)}
            loading={deleting}
          />
          <DeleteModal
            show={deleteMultipleModal}
            onDeleteClick={() => handleDeleteMultiple()}
            onCloseClick={() => setDeleteMultipleModal(false)}
            loading={deleting}
          />
          <Permissions checks={[{ resource: "contractor", action: "delete" }]}>
            <BulkActionBar
              count={selectedCheckBox.length}
              onClear={unCheckAll}
              itemLabel="contract"
            >
              <Button
                color="danger"
                size="sm"
                className="material-shadow-none"
                onClick={() => setDeleteMultipleModal(true)}
              >
                <i className="ri-delete-bin-line align-bottom me-1"></i> Delete
              </Button>
            </BulkActionBar>
          </Permissions>
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default AllContracts;
